import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useUpdateCompanyMutation,
  useFetchCompanyDraftEditMutation,
  useGetCompanyRequestStatusMutation,
} from "../../../../store/Company/companyApiSlice";
import { setCurrentEditingCompany } from "../../../../store/Company/companySlice";
import { SITE_URL } from "../../../../constants/apiUrls";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { CommonToast } from "../../../common/Toast";
import { ASSET_SPECS } from "./imageAssets";
import { nextStatus } from "./companyDraftSave";

/* Logo & Banner — the section's behaviour. */

/* Transcribed from fetchDraftDetails, typo included. */
export const draftAssetUrl = (field, value) => {
  if (!value) return null;
  return field === "banner" ? `${SITE_URL}io/media/${value}` : `${SITE_URL}/media/${value}`;
};

export const useLogoBanner = ({ onSaved }) => {
  const dispatch = useDispatch();
  const { currentEditingCompany } = useSelector((state) => state.company);
  const { token } = useSelector((state) => state.auth);

  /* What is on screen, per asset, and the file waiting to be uploaded. */
  const [previews, setPreviews] = useState({ banner: null, logo: null });
  const [files, setFiles] = useState({ banner: null, logo: null });
  const [verificationStatus, setVerificationStatus] = useState("pending");

  const [updateCompany, { isLoading }] = useUpdateCompanyMutation();
  const [fetchCompanyDraftEdit] = useFetchCompanyDraftEditMutation();
  const [getCompanyRequestStatus] = useGetCompanyRequestStatusMutation();

  const companyID = currentEditingCompany?.companyID;

  /* Transcribed: a draft's image wins over the record's, per asset. */
  const loadDraft = async () => {
    try {
      const fetched = await fetchCompanyDraftEdit({ companyId: companyID, token }).unwrap();
      if (fetched?.data) {
        const row = fetched.data[0];
        setPreviews({
          logo: draftAssetUrl("logo", row?.logo) || currentEditingCompany?.logo,
          banner: draftAssetUrl("banner", row?.banner) || currentEditingCompany?.banner,
        });
      }
    } catch {
      /* Swallowed in the live code. The record's own images stay on screen,
         which the branch below has already put there. */
    }
  };

  useEffect(() => {
    if (!currentEditingCompany) return;

    /* Transcribed: the latest request for THIS company. */
    if (currentEditingCompany?.userCompany) {
      (async () => {
        const individualID =
          localStorage.getItem("userindividualID") ||
          currentEditingCompany?.userCompany?.[0]?.individualID?.individualID;

        const res = await getCompanyRequestStatus({
          companyID: currentEditingCompany.companyID,
          individualID,
          token,
        });

        const rows = res?.data?.filter(
          (item) => item?.companyID?.companyID === currentEditingCompany.companyID
        );
        if (rows?.length) {
          setVerificationStatus(rows[rows.length - 1]?.status);
        }
      })();
    }

    if (verificationStatus === "Modified") {
      loadDraft();
    } else {
      setPreviews({
        logo: currentEditingCompany.logo,
        banner: currentEditingCompany.banner,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEditingCompany, verificationStatus]);

  /* A chosen file previews immediately and is held for the submit. The live
     screen reads it through a FileReader; an object URL does the same job
     without the async hop, and is revoked when it is replaced. */
  const setAsset = (key, file) => {
    if (!file) return;
    setFiles((prev) => ({ ...prev, [key]: file }));
    setPreviews((prev) => {
      if (prev[key]?.startsWith?.("blob:")) URL.revokeObjectURL(prev[key]);
      return { ...prev, [key]: URL.createObjectURL(file) };
    });
  };

  /* Transcribed: only the assets that were actually changed are uploaded, and
     the status field is appended on every submit. */
  const submit = async () => {
    try {
      const formData = new FormData();

      for (const spec of [ASSET_SPECS.banner, ASSET_SPECS.logo]) {
        const file = files[spec.key];
        if (file == null) continue;

        const uploaded = await uploadFileToS3(
          uploadFilePath?.[spec.uploadPath],
          file,
          token,
          companyID,
          spec.category,
          "",
          true
        );

        /* HOW `logo: "undefined"` GOT INTO THE DATABASE. */
        if (!uploaded?.url) {
          throw new Error(`Couldn't upload the ${spec.key}. Please try again.`);
        }
        formData.append(spec.field, uploaded.url);
      }

      formData.append(
        "banner_logo_license_status",
        nextStatus(currentEditingCompany?.banner_logo_license_status)
      );

      const res = await updateCompany({
        data: formData,
        token,
        companyId: companyID,
      }).unwrap();

      /* Transcribed: the live check is on `res?.companyID`, not merely a
         truthy response. */
      if (res?.companyID) {
        dispatch(setCurrentEditingCompany(res));
        setFiles({ banner: null, logo: null });
        onSaved?.();
      }
    } catch (error) {
      /* The live catch reports AND advances, so a failed upload silently moved the viewer to the next section as though it had saved. */
      CommonToast.error(
        error?.data?.message || error?.message || "Couldn't save the logo and banner."
      );
    }
  };

  return {
    previews,
    setAsset,
    /* Whether there is anything new to upload. */
    isDirty: Boolean(files.banner || files.logo),
    isLoading,
    submit,
  };
};

export default useLogoBanner;
